import React, { useCallback, useEffect, useState } from "react";
import styles from './ClinicalSiteForm.module.scss';
import PropTypes from "prop-types";
import TextField from "../textField/TextField";
import Dropdown from "../dropdown/Dropdown";
import MultiSelectDropdown from "../multiSelectDropdown/MultiSelectDropdown";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import Tag from "../tag/Tag";
import { getSpecialityTagVariant } from "../../utils/specialityUtils";
import { formatPhoneNumber } from "../../utils/stringUtils";
import { useNavigate } from "react-router-dom";

const ClinicalSiteForm = ({
  formData,
  specialityList = [],
  healthSystemList = [],
  yearList = [],
  onDataChange,
  onSubmit,
  isEdit = false
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [isValidated, setIsValidated] = useState(false);

  const [isAddingSpecialty, setIsAddingSpecialty] = useState(false);
  const [newSpecialtyId, setNewSpecialtyId] = useState('');
  const [newSpecialtyCapacity, setNewSpecialtyCapacity] = useState('');
  const [specialtyErrors, setSpecialtyErrors] = useState({});

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate Name
    if (!formData.name?.trim()) {
      newErrors.name = 'Clinical Site Name is required';
    }

    // Validate Address
    if (!formData.address?.trim()) {
      newErrors.address = 'Primary Address is required';
    }

    // Validate Email
    if (!formData.email?.trim()) {
      newErrors.email = 'Email Address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate Site Phone
    const cleanPhone = formData.phone?.replace(/\D/g, '') || '';
    if (!formData.phone?.trim()) {
       newErrors.phone = 'Phone Number is required';
    } else if (!/^\d{10}$/.test(cleanPhone)) {
      newErrors.phone = 'Invalid phone number format. Must be 10 digits.';
    }

    // Validate Health System
    if (!formData.healthSystemId) {
      newErrors.healthSystemId = 'Health System is required';
    }

    // Validate Associated Year
    if (!formData.supportedYearIds || formData.supportedYearIds.length === 0) {
      newErrors.supportedYearIds = 'Associated Year is required';
    }

    // Validate Contact Person
    if (!formData.contactPerson?.trim()) {
      newErrors.contactPerson = 'Contact Person is required';
    }

    // Validate Designation
    if (!formData.designation?.trim()) {
      newErrors.designation = 'Designation is required';
    }

    // Validate Contact Phone
    const cleanContactPhone = formData.contactPhone?.replace(/\D/g, '') || '';
    if (!formData.contactPhone?.trim()) {
      newErrors.contactPhone = 'Phone is required';
    } else if (!/^\d{10}$/.test(cleanContactPhone)) {
      newErrors.contactPhone = 'Invalid phone number format. Must be 10 digits.';
    }

    // Validate Contact Email
    if (!formData.contactEmail?.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length > 0 ? Object.keys(newErrors)[0] : null;

  }, [formData]);

  const scrollToFirstError = (field) => {
    const headerHeight = 72; 
    const element = document.querySelector(`[data-error-field="${field}"]`);
    if (element) {
      const elementTop = element.offsetTop - headerHeight; 
      window.scrollTo({
        top: elementTop,
        behavior: 'smooth',
      });
    }
  };

  const handleSubmit = () => {
    const firstErrorField = validateForm();
    if (firstErrorField) {
      setIsValidated(true);
      scrollToFirstError(firstErrorField);
      return false;
    }
    onSubmit();
  };

  const handleAddSpecialtyClick = () => {
    setIsAddingSpecialty(true);
    setNewSpecialtyId('');
    setNewSpecialtyCapacity('');
    setSpecialtyErrors({});
  };

  const handleCancelAddSpecialty = () => {
    setIsAddingSpecialty(false);
    setNewSpecialtyId('');
    setNewSpecialtyCapacity('');
    setSpecialtyErrors({});
  };

  const handleAddSpecialtyConfirm = () => {
    const errorList = {};
    if (!newSpecialtyId) errorList.specialty = 'Specialty is required';
    if (!newSpecialtyCapacity || isNaN(newSpecialtyCapacity) || Number(newSpecialtyCapacity) <= 0) {
      errorList.capacity = 'Valid capacity is required';
    }

    if (Object.keys(errorList).length > 0) {
      setSpecialtyErrors(errorList);
      return;
    }

    const existing = formData.specialities?.find(s => s.specialityId === newSpecialtyId);
    if (existing) {
       setSpecialtyErrors({ specialty: 'Specialty already added' });
       return;
    }

    const newSpecialty = {
      specialityId: newSpecialtyId,
      numberOfStudents: Number(newSpecialtyCapacity)
    };

    onDataChange({
      specialities: [...(formData.specialities || []), newSpecialty]
    });

    handleCancelAddSpecialty();
  };

  const handleRemoveSpecialty = (spId) => {
    onDataChange({
      specialities: (formData.specialities || []).filter(s => s.specialityId !== spId)
    });
  };

  const getSpecialityName = (id) => {
    const sp = specialityList.find(s => s.value === id);
    return sp ? sp.label : '';
  };

  useEffect(() => {
    if (isValidated) {
      validateForm();
    }
  }, [formData, isValidated, validateForm]);

  const handleChange = (field, value) => {
    let finalValue = value;
    if (field === 'phone' || field === 'contactPhone') {
      finalValue = formatPhoneNumber(value);
    }
    
    onDataChange({
      [field]: finalValue
    });
  };

  return (
    <div className={styles.form}>
      <h2>Clinical Site Details</h2>
      
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="name">
          <TextField 
            label="Clinical Site Name"
            value={formData.name}
            onChange={(value) => handleChange('name', value)}
            error={errors.name}
          />
        </div>
        <div className={styles.column50}>
          <Dropdown 
            label="Clinical Site Status"
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]}
            value={formData.isActive ? 'Active' : 'Inactive'}
            onChange={(value) => handleChange('isActive', value === 'Active')}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.column} data-error-field="address">
          <TextField 
            label="Primary Address"
            value={formData.address}
            onChange={(value) => handleChange('address', value)}
            error={errors.address}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.column50} data-error-field="email">
          <TextField 
            label="Email Address"
            value={formData.email}
            onChange={(value) => handleChange('email', value)}
            error={errors.email}
          />
        </div>
        <div className={styles.column50} data-error-field="phone">
          <TextField 
            label="Phone Number"
            value={formData.phone}
            onChange={(value) => handleChange('phone', value)}
            error={errors.phone}
          />
        </div>
      </div>

      <div className={styles.row}>
         <div className={styles.column50}>
          <TextField 
            label="Website URL (Optional)"
            value={formData.website}
            onChange={(value) => handleChange('website', value)}
          />
        </div>
         <div className={styles.column50} data-error-field="healthSystemId">
          <Dropdown
            label="Health System"
            options={healthSystemList}
            value={formData.healthSystemId}
            onChange={(value) => handleChange('healthSystemId', value)}
            error={errors.healthSystemId}
            disabled={isEdit}
          />
        </div>
      </div>
       
      <div className={styles.row}>
        <div className={styles.column} data-error-field="supportedYearIds">
           <MultiSelectDropdown
            label="Associated Year"
            options={yearList}
            selected={formData.supportedYearIds}
            onChange={(ids) => handleChange('supportedYearIds', ids)}
            error={errors.supportedYearIds}
          />
        </div>
      </div>

      <h2>Specialty</h2>
      
      <div className={styles.specialtySection}>
        {(!formData.specialities || formData.specialities.length === 0) && !isAddingSpecialty && (
             <div className={styles.addMoreBtnWrapper}>
                <Button 
                    label={
                        <div className={styles.cta}>
                            <Icon name="plus" />
                            Add Specialty
                        </div>
                    }
                    variant="primary-light"
                    size="medium"
                    onClick={handleAddSpecialtyClick}
                />
            </div>
        )}

        {formData.specialities && formData.specialities.length > 0 && (
            <div className={styles.specialtyList}>
                {formData.specialities.map((spec) => {
                    const name = getSpecialityName(spec.specialityId);
                    return (
                        <div key={spec.specialityId} className={styles.specialtyRowView}>
                            <div className={styles.specialtyColumn}>
                                {spec === formData.specialities[0] && <label>Specialty</label>}
                                <div className={styles.specialtyBox}>
                                    <Tag text={name} variant={getSpecialityTagVariant(name)} rounded={true} />
                                </div>
                            </div>
                            <div className={styles.capacityColumn}>
                                {spec === formData.specialities[0] && <label>Capacity</label>}
                                <div className={styles.capacityBox}>
                                    {spec.numberOfStudents}
                                </div>
                            </div>
                            <div className={styles.actionColumn}>
                                <button className={styles.deleteBtn} onClick={() => handleRemoveSpecialty(spec.specialityId)}>
                                    <Icon name="trash" />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        )}

        {formData.specialities && formData.specialities.length > 0 && !isAddingSpecialty && (
             <div className={styles.addMoreBtnWrapper}>
                <Button 
                    label={
                        <div className={styles.cta}>
                            <Icon name="plus" />
                            Add Specialty
                        </div>
                    }
                    variant="primary-light"
                    size="medium"
                    onClick={handleAddSpecialtyClick}
                />
             </div>
        )}

        {isAddingSpecialty && (
            <div className={styles.specialtyFormBlock}>
                <div className={styles.row}>
                    <div className={styles.column50}>
                        <Dropdown 
                            label="Specialty"
                            options={specialityList.filter(s => !formData.specialities?.find(fs => fs.specialityId === s.value))}
                            value={newSpecialtyId}
                            onChange={(val) => setNewSpecialtyId(val)}
                            error={specialtyErrors.specialty}
                            placeholder="Select Specialty"
                        />
                    </div>
                    <div className={styles.column50}>
                        <TextField 
                            label="Capacity"
                            value={newSpecialtyCapacity}
                            onChange={(val) => setNewSpecialtyCapacity(val)}
                            error={specialtyErrors.capacity}
                            type="number"
                        />
                    </div>
                </div>
                <div className={styles.formActions}>
                    <Button label="Add" variant="primary" size="medium" onClick={handleAddSpecialtyConfirm} />
                    <Button label="Cancel" variant="grey" size="medium" onClick={handleCancelAddSpecialty} />
                </div>
            </div>
        )}
      </div>

      <h2>Primary Contact Details</h2>

      <div className={styles.row}>
        <div className={styles.column50} data-error-field="contactPerson">
          <TextField 
            label="Contact Person"
            value={formData.contactPerson}
            onChange={(value) => handleChange('contactPerson', value)}
            error={errors.contactPerson}
          />
        </div>
        <div className={styles.column50} data-error-field="designation">
          <TextField 
            label="Designation"
            value={formData.designation}
            onChange={(value) => handleChange('designation', value)}
            error={errors.designation}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.column50} data-error-field="contactPhone">
          <TextField 
            label="Phone"
            value={formData.contactPhone}
            onChange={(value) => handleChange('contactPhone', value)}
            error={errors.contactPhone}
          />
        </div>
        <div className={styles.column50} data-error-field="contactEmail">
          <TextField 
            label="Email"
            value={formData.contactEmail}
            onChange={(value) => handleChange('contactEmail', value)}
            error={errors.contactEmail}
          />
        </div>
      </div>

      <div className={styles.actions}>
        <Button 
          label="Cancel" 
          variant="secondary" 
          onClick={() => navigate('/clinical-site')} 
        />
        <Button 
          label="Save" 
          onClick={handleSubmit} 
        />
      </div>

    </div>
  );
};

ClinicalSiteForm.propTypes = {
  formData: PropTypes.object.isRequired,
  specialityList: PropTypes.array,
  healthSystemList: PropTypes.array,
  yearList: PropTypes.array,
  onDataChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool
};

export default ClinicalSiteForm;
